<template>
    <div class="mt-2">
      <!-- Navigation Tabs -->
      <ul class="nav nav-tabs main-tabs">
        <li v-for="(tab, index) in tabs" :key="index" class="nav-item">
          <a
            class="nav-link tab-item"
            :class="{
              active: activeTab === index
            }"
            href="#"
            @click.prevent="selectTab(index)"
          >
            <i :class="['bi', tab.icon, 'tab-icon']"></i>
            <span class="tab-label">{{ tab.label }}</span>
          </a>
        </li>
      </ul>
  
      <!-- Tab Content -->
      <div class="tab-content">
        <component
          :is="activeTabComponent"
          v-bind="getComponentProps(activeTabComponent)"
        />
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed } from "vue";
  import { useUserStore } from "../stores/userStore.js";
  import WasplTest from "./mainTabSystem/WasplTest.vue";
  import ItemBank from "./mainTabSystem/ItemBank.vue";
  import PublicationManager from "./mainTabSystem/PublicationManager.vue";
  import GroupManager from "./mainTabSystem/GroupManager.vue";
  import Results from "./mainTabSystem/Results.vue";
  import About from "./mainTabSystem/About.vue";
  import DevTools from "./mainTabSystem/DevTools.vue";
  
  const props = defineProps({
    //Todo
  });

  // Accès au store utilisateur
  const userStore = useUserStore();
  
  // Computed pour vérifier si l'utilisateur peut accéder aux dev tools
  const canAccessDevTools = computed(() => {
    const user = userStore.user;
    return user && (user.role === 'Admin' || user.role === 'Author');
  });
  
  // Define tabs with their labels, icons and components
  const baseTabs = [
    { label: "Test Manager", icon: "bi-clipboard-check", component: WasplTest },
    { label: "Item Collection", icon: "bi-collection", component: ItemBank },
    { label: "Publication", icon: "bi-megaphone", component: PublicationManager },
    { label: "Group Manager", icon: "bi-people", component: GroupManager },
    { label: "Results", icon: "bi-bar-chart-line", component: Results },
    { label: "About", icon: "bi-info-circle", component: About },
  ];

  // Tabs avec DevTools conditionnel
  const tabs = computed(() => {
    const allTabs = [...baseTabs];
    
    if (canAccessDevTools.value) {
      allTabs.push({
        label: "Dev Tools",
        icon: "bi-tools",
        component: DevTools,
        devOnly: true
      });
    }
    
    return allTabs;
  });
  
  // Active tab index
  const activeTab = ref(0);
  
  // Computed property to determine the active component
  const activeTabComponent = computed(() => tabs.value[activeTab.value]?.component);
  
  // Function to provide props based on the component
  const getComponentProps = (component) => {
    if (component === WasplTest) {
      return {
        prompt: "This is an example", // Example to pass props
      };
    }
    return {}; // No props for other components
  };
  
  // Method to select a tab
  function selectTab(index) {
    activeTab.value = index;
  }
  </script>
  
  <style scoped>
  /* Main tabs container */
  .main-tabs {
    border-bottom: 2px solid #E0E0E0;
    gap: 8px;
    padding: 0;
    margin-bottom: 0;
  }

  /* Tab item base styles */
  .tab-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    border-bottom: 4px solid transparent;
    background-color: transparent;
    color: #757575;
    font-weight: 500;
    font-size: 0.95rem;
    transition: all 0.25s ease;
    border-radius: 8px 8px 0 0;
    position: relative;
  }

  /* Tab icon */
  .tab-icon {
    font-size: 1.6rem;
    transition: transform 0.25s ease;
  }

  /* Tab label */
  .tab-label {
    
    white-space: nowrap;
  }

  /* Hover state for inactive tabs */
  .tab-item:not(.active):hover {
    background-color: #F5F5F5;
    color: #424242;
    border-bottom-color: #90CAF9;
  }

  .tab-item:not(.active):hover .tab-icon {
    transform: translateY(-2px);
  }

  /* Active tab styles */
  .tab-item.active {
    background-color: #E3F2FD;
    color: #1565C0;
    font-weight: 600;
    border-bottom-color: #1976D2;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
  }

  .tab-item.active .tab-icon {
    color: #1976D2;
  }

  /* Remove Bootstrap default borders */
  .nav-tabs .nav-item {
    margin-bottom: 0;
  }

  .nav-tabs .nav-link {
    border: none;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .main-tabs {
      overflow-x: auto;
      flex-wrap: nowrap;
      -webkit-overflow-scrolling: touch;
    }

    .tab-item {
      padding: 10px 16px;
      font-size: 0.9rem;
    }

    .tab-icon {
      font-size: 1rem;
    }
  }

  @media (max-width: 576px) {
    .tab-label {
      display: none;
    }

    .tab-item {
      padding: 12px;
      min-width: 48px;
      justify-content: center;
    }

    .tab-icon {
      font-size: 1.2rem;
    }
  }
  </style>
  